import { FormField, FormResponses } from '../types/form.types';

/**
 * Client-side validation hook
 */
export const useFormValidation = () => {
    /**
     * Validate a single field value
     */
    const validateField = (
        field: FormField,
        value: string | number | string[] | undefined
    ): string | null => {
        const normalizedType = field.type.trim().toLowerCase();
        if (normalizedType === 'statictext' || normalizedType === 'static_text') {
            return null;
        }

        const isEmptyValue =
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0);

        // Check required fields
        if (field.required && isEmptyValue) {
            return 'This field is required';
        }

        // Skip validation if field is empty and not required
        if (isEmptyValue) {
            return null;
        }

        if (normalizedType === 'file') {
            const fileIds = Array.isArray(value)
                ? value
                : typeof value === 'string'
                    ? [value]
                    : [];
            const maxFiles = field.validation?.maxFiles;

            if (maxFiles && fileIds.length > maxFiles) {
                return field.validation?.errorMessage || `Maximum ${maxFiles} files allowed`;
            }

            return null;
        }

        const validation = field.validation;
        if (!validation) return null;

        const stringValue = String(value);

        // Min length validation
        if (validation.minLength && stringValue.length < validation.minLength) {
            return validation.errorMessage || `Must be at least ${validation.minLength} characters`;
        }

        // Max length validation
        if (validation.maxLength && stringValue.length > validation.maxLength) {
            return validation.errorMessage || `Must be no more than ${validation.maxLength} characters`;
        }

        // Pattern validation
        if (validation.pattern) {
            const regex = new RegExp(validation.pattern);
            if (!regex.test(stringValue)) {
                return validation.errorMessage || 'Invalid format';
            }
        }

        // Email validation
        if (field.type === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(stringValue)) {
                return 'Please enter a valid email address';
            }
        }

        return null;
    };

    /**
     * Validate multiple fields in a step
     */
    const validateFields = (
        fields: FormField[],
        responses: FormResponses,
        visibleFieldIds: string[]
    ): { [fieldId: string]: string } => {
        const errors: { [fieldId: string]: string } = {};

        fields.forEach((field) => {
            // Only validate visible fields
            if (!visibleFieldIds.includes(field.fieldId)) {
                return;
            }

            const value = responses[field.fieldId];
            const error = validateField(field, value);

            if (error) {
                errors[field.fieldId] = error;
            }

        });

        return errors;
    };

    /**
     * Check if a field should be visible based on conditional logic
     */
    const isFieldVisible = (field: FormField, responses: FormResponses): boolean => {
        if (!field.conditionalLogic) {
            return true;
        }

        const { fieldId, operator, value: conditionValue } = field.conditionalLogic.showIf;
        const actualValue = responses[fieldId];
        const normalizedValues = Array.isArray(actualValue)
            ? actualValue.map((item) => String(item))
            : typeof actualValue === 'string'
                ? [actualValue]
                : actualValue === undefined || actualValue === null
                    ? []
                    : [String(actualValue)];

        switch (operator) {
            case 'equals':
                return actualValue === conditionValue;
            case 'not_equals':
                return actualValue !== conditionValue;
            case 'contains':
                return Array.isArray(actualValue)
                    ? actualValue.map((item) => String(item)).includes(String(conditionValue))
                    : String(actualValue).includes(String(conditionValue));
            case 'in':
                return Array.isArray(conditionValue)
                    ? conditionValue.map((item) => String(item)).some((item) => normalizedValues.includes(item))
                    : false;
            case 'count_gte': {
                const minimumCount = Number(conditionValue);
                if (Number.isNaN(minimumCount)) {
                    return false;
                }
                return normalizedValues.length >= minimumCount;
            }
            default:
                return false;
        }
    };

    /**
     * Get list of visible field IDs based on conditional logic
     */
    const getVisibleFieldIds = (fields: FormField[], responses: FormResponses): string[] => {
        return fields
            .filter((field) => isFieldVisible(field, responses))
            .map((field) => field.fieldId);
    };

    return {
        validateField,
        validateFields,
        isFieldVisible,
        getVisibleFieldIds
    };
};
